import { Heart, Moon, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { AnimatedCard } from "@/components/animations/AnimatedCard";

const benefits = [
  {
    icon: Heart,
    title: "Pure Copper",
    description:
      "All our accessories are crafted from pure copper, a material traditionally associated with balance and wellness.",
  },
  {
    icon: Moon,
    title: "Integrated Magnets",
    description:
      "Discreet magnets are built into each piece for a gentle, continuous presence throughout your day.",
  },
  {
    icon: Zap,
    title: "Adjustable Design",
    description:
      "Rings and bracelets are fully adjustable to perfectly fit your shape, for both men and women.",
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

export const BenefitsSection = () => {
  return (
    <section className="section-padding bg-background">
      <div className="container-calim">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-12 md:mb-16 max-w-2xl mx-auto"
        >
          <motion.p variants={fadeInUp} className="text-subhead mb-3 md:mb-4">
            Our Commitments
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-headline mb-4 md:mb-6">
            Quality & Craftsmanship
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-body-lg text-base md:text-lg lg:text-xl">
            Every CALMISTYLE accessory is thoughtfully designed, 
            combining noble materials with minimalist aesthetics.
          </motion.p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-12">
          {benefits.map((benefit, index) => (
            <AnimatedCard
              key={benefit.title}
              delay={index * 0.1}
              hoverScale={1.03}
              className="card-calim text-center"
            >
              <motion.div
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center justify-center w-12 md:w-14 h-12 md:h-14 bg-secondary rounded-sm mb-4 md:mb-6"
              >
                <benefit.icon className="h-5 md:h-6 w-5 md:w-6 text-foreground" />
              </motion.div>
              <h3 className="font-display text-lg md:text-xl mb-3 md:mb-4">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {benefit.description}
              </p>
            </AnimatedCard>
          ))}
        </div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-xs text-muted-foreground text-center mt-10 md:mt-12 max-w-2xl mx-auto"
        >
          CALMISTYLE is a wellness accessory and does not constitute a medical device.
          Results may vary from person to person.
        </motion.p>
      </div>
    </section>
  );
};
